import { playEventsResponseSchema, type PlayEvent } from "@libs/playback-contracts";
import type { PlayOutboxStorage, StoredEvent } from "./createPlayOutboxStorage";
import type { PlayCredentials, PlaySink } from "./streamTracker";

/**
 * What the outbox reaches outside itself. The app wires these to tRPC, the
 * device, TanStack's online manager and React Native's AppState
 * (the data session); this module imports none of them, so its test drains a
 * real outbox at the play-events contract (`playOutboxCore.web.test.ts`).
 */
export type PlayOutboxPorts = {
  storage: PlayOutboxStorage;
  /** Sends one batch to the play-events endpoint; the reply is validated here. */
  send(request: { device_id: string; events: PlayEvent[] }): Promise<unknown>;
  deviceId(): Promise<string>;
  newPlayId(): string;
  /** The signed-in account: plays it starts are its own, and only its plays are sent. */
  owner(): string | undefined;
  online: {
    isOnline(): boolean;
    subscribe(listener: (online: boolean) => void): () => void;
  };
  /** Calls `listener` each time the app returns to the foreground; returns the unsubscribe. */
  onForeground(listener: () => void): () => void;
  /** Runs before each online drain; the app replays its pre-outbox queue here. */
  beforeDrain(): Promise<void>;
  /** Reports the first failed storage write of each run of failures. */
  reportStorageError(error: unknown): void;
  /** Runs `drain` once no other outbox over the same storage is draining it,
   *  so they send one after another. Web tabs share their storage. */
  exclusive?(drain: () => Promise<void>): Promise<void>;
};

const BACKOFF = [10_000, 30_000, 60_000, 300_000];
const STORE_RETRY_MS = 1000;
const TERMINAL_ERRORS = new Set([
  "play_closed",
  "play_not_found",
  "invalid_play_token",
  "client_timestamp_out_of_range",
  "client_timestamp_future",
  "song_not_found",
  "invalid_event",
]);

export function createPlayOutbox({
  storage,
  send,
  deviceId: resolveDeviceId,
  newPlayId,
  owner: currentOwner,
  online,
  onForeground,
  beforeDrain,
  reportStorageError,
  exclusive = (drain) => drain(),
}: PlayOutboxPorts) {
  const owner = () => currentOwner() ?? null;
  let running = false;
  let disposed = false;
  let deviceId: string | undefined;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let timerAt = Infinity;
  let generation = 0;
  let flushing: Promise<void> | undefined;
  // The flush in `flushing` still waits for its turn (`exclusive`).
  let waiting = false;
  // Events accepted but not yet stored, oldest first, each with the account
  // that recorded it. A failed write (another JS runtime holding the database
  // lock) keeps the exact event, id, seq and timestamps included, and writes it
  // again before any later event, so the tracker's play ids, sequence and
  // timers never depend on storage. Until stored, these live only in memory.
  let unsaved: { event: StoredEvent; owner: string | null }[] = [];
  let storeTimer: ReturnType<typeof setTimeout> | undefined;
  let storeFailing = false;
  let unsubscribeOnline: (() => void) | undefined;
  let unsubscribeForeground: (() => void) | undefined;
  let listener:
    | ((
        id: string,
        credentials: PlayCredentials,
        acceptedStart: boolean,
      ) => void)
    | undefined;
  function schedule(delay: number) {
    if (!running || !online.isOnline()) return;
    const at = Date.now() + delay;
    if (timer && at >= timerAt) return;
    clearTimeout(timer);
    timerAt = at;
    timer = setTimeout(() => {
      timer = undefined;
      void flush().catch(console.error);
    }, delay);
  }
  const payload = (event: StoredEvent): PlayEvent => {
    const { id: _id, created_at: _at, ...body } = event;
    const token = storage.getPlay(event.play_id)?.token || event.token;
    return { ...body, ...(token ? { token } : {}) };
  };
  async function drain(
    epoch: number,
    retryToken = true,
    progress = false,
  ): Promise<void> {
    const before = storage.size(owner());
    storage.prune(Date.now() - 86400000);
    progress ||= storage.size(owner()) < before;
    if (progress) failures = 0;
    if (!online.isOnline()) return;
    await beforeDrain();
    if (epoch !== generation) return;
    const events = storage.select(owner());
    if (!events.length) return;
    try {
      deviceId = await resolveDeviceId();
      if (epoch !== generation || !deviceId.trim()) return;
      const response = playEventsResponseSchema.parse(
        await send({ device_id: deviceId, events: events.map(payload) }),
      );
      if (epoch !== generation) return;
      const results = new Map(
        response.results.map((r) => [`${r.play_id}:${r.seq}`, r]),
      );
      const awaitingToken = (e: StoredEvent) =>
        retryToken &&
        results.get(`${e.play_id}:${e.seq}`)?.error === "invalid_play_token" &&
        Boolean(response.plays[e.play_id]?.token);
      const removed = events.filter((e) => {
        const result = results.get(`${e.play_id}:${e.seq}`);
        return (
          result &&
          (result.accepted ||
            result.duplicate ||
            (TERMINAL_ERRORS.has(result.error ?? "") && !awaitingToken(e)))
        );
      });
      storage.acknowledge(
        removed.map((e) => e.id),
        response.plays,
      );
      progress ||= removed.length > 0;
      for (const [id, credentials] of Object.entries(response.plays)) {
        listener?.(
          id,
          { ...credentials, playback_id: id },
          events.some(
            (e) =>
              e.play_id === id &&
              e.kind === "start" &&
              (results.get(`${id}:${e.seq}`)?.accepted ||
                results.get(`${id}:${e.seq}`)?.duplicate),
          ),
        );
      }
      const retry = events.some(awaitingToken);
      const onlyTokenErrors = events.every(
        (e) => removed.includes(e) || awaitingToken(e),
      );
      failures = progress || (retry && onlyTokenErrors) ? 0 : failures + 1;
      // Exactly one immediate retry with fresh credentials, never a retry loop.
      if (retry) await drain(epoch, false, progress);
    } catch {
      // Envelope/response rejection says nothing about individual rows.
      if (epoch === generation) failures = progress ? 0 : failures + 1;
    }
  }
  /** `now` (logout) sends at once rather than wait for another outbox's turn;
   *  a drain already running here is still awaited. */
  function flush(now = false): Promise<void> {
    if (disposed) return Promise.resolve();
    if (flushing && !(now && waiting)) return flushing;
    clearTimeout(timer);
    timer = undefined;
    const epoch = generation;
    const run = () => {
      waiting = false;
      return drain(epoch);
    };
    waiting = !now;
    const pending = (now ? run() : exclusive(run)).finally(() => {
      if (epoch !== generation || flushing !== pending) return;
      flushing = undefined;
      if (storage.size(owner()))
        schedule(failures ? BACKOFF[Math.min(failures - 1, 3)] : 1000);
    });
    flushing = pending;
    return pending;
  }
  /** Abandons work in flight for the signed-in account; its unsent plays stay
   *  stored for its next sign-in. */
  function detach() {
    if (disposed) return;
    // A timed-out request must not update credentials, notify listeners or retry
    // after logout, even if a new account has already queued its own events.
    generation++;
    clearTimeout(timer);
    timer = undefined;
    flushing = undefined;
    failures = 0;
    deviceId = undefined;
    settleUnsaved();
  }

  /** Writes the unsaved events in order; on failure retries every second. */
  function store() {
    clearTimeout(storeTimer);
    storeTimer = undefined;
    try {
      while (unsaved.length) {
        storage.enqueue(unsaved[0].event, unsaved[0].owner); // Synchronous: survives immediately after the player transition.
        unsaved.shift();
      }
    } catch (error) {
      if (!storeFailing) reportStorageError(error);
      storeFailing = true;
      storeTimer = setTimeout(store, STORE_RETRY_MS);
      return;
    }
    storeFailing = false;
    // Every event, including a web tab-close stop, leaves only through tRPC on
    // the next flush; the server credits a late stop by evidence.
    schedule(1000);
  }
  /** One last write of what is still in memory; what fails is dropped with it. */
  function settleUnsaved() {
    clearTimeout(storeTimer);
    storeTimer = undefined;
    try {
      for (const pending of unsaved)
        storage.enqueue(pending.event, pending.owner);
    } catch {
      // Still locked: memory-only events end with this session.
    }
    unsaved = [];
    storeFailing = false;
  }

  function enqueue(event: PlayEvent) {
    if (disposed) return;
    unsaved.push({
      event: {
        ...event,
        id: `${event.play_id}:${event.seq}`,
        created_at: Date.now(),
      },
      owner: owner(),
    });
    store();
  }
  const sink: PlaySink = {
    start(context, position = 0) {
      deviceId = context.device_id;
      const id = newPlayId();
      enqueue({
        play_id: id,
        seq: 0,
        kind: "start",
        client_ts: new Date().toISOString(),
        position,
        song_id: context.song_id,
        collection: context.collection,
        ...(context.country_code ? { country_code: context.country_code } : {}),
      });
      return id;
    },
    heartbeat(id, seq, position, seeked) {
      enqueue({
        play_id: id,
        seq,
        kind: "heartbeat",
        client_ts: new Date().toISOString(),
        position,
        ...(seeked ? { seeked } : {}),
      });
    },
    stop(id, seq, position, reason, seeked) {
      enqueue({
        play_id: id,
        seq,
        kind: "stop",
        client_ts: new Date().toISOString(),
        position,
        reason,
        ...(seeked ? { seeked } : {}),
      });
    },
  };
  return {
    sink,
    enqueue,
    flush,
    detach,
    /** Deletes the signed-in account's unsent plays, once nobody can send them. */
    forgetAccount() {
      const account = currentOwner();
      if (account) storage.forget(account);
    },
    setListeners(onResult?: typeof listener) {
      listener = onResult;
    },
    start() {
      if (disposed) return;
      running = true;
      unsubscribeOnline?.();
      unsubscribeOnline = online.subscribe((isOnline) => {
        if (isOnline) void flush().catch(console.error);
      });
      unsubscribeForeground?.();
      unsubscribeForeground = onForeground(
        () => void flush().catch(console.error),
      );
      void flush().catch(console.error);
    },
    dispose() {
      settleUnsaved();
      disposed = true;
      running = false;
      generation++;
      clearTimeout(timer);
      timer = undefined;
      flushing = undefined;
      listener = undefined;
      unsubscribeOnline?.();
      unsubscribeForeground?.();
    },
    stop() {
      running = false;
      clearTimeout(timer);
      timer = undefined;
      unsubscribeOnline?.();
      unsubscribeForeground?.();
      void flush().catch(console.error);
    },
    get size() {
      const account = owner();
      return (
        storage.size(account) +
        unsaved.filter((pending) => pending.owner === account).length
      );
    },
  };
}
